import { Box } from '@mui/material';
import { CrewField } from '../RaptorJobCreateModal';
import { useERPCrewSelector } from '../hook/useERPCrewSelector';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { GroupEmployee, ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { crewMemberHasJobRole } from '@/raptorjob/presentation/utils/crewRoleAssignment';
import { CrewComplianceWarningAlert } from './CrewComplianceWarningAlert';
import { CrewFieldRow } from './CrewFieldRow';

interface EmployeeResource {
  id: number;
}

interface ERPCrewSelectorProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  startDate?: Date | null;
  /** When set, crew list excludes employees restricted from this customer. */
  customerId?: number;
  /** When set, crew list excludes employees restricted from this job site. */
  jobSiteId?: number;
  isEmployeeCompliant?: (employee: EmployeeResource, roleId: number) => boolean;
  getEmployeeMissingCerts?: (employee: EmployeeResource, roleId: number) => string[];
}

export const ERPCrewSelector = ({
  crewFields,
  setCrewFields,
  startDate,
  customerId,
  jobSiteId,
  isEmployeeCompliant,
  getEmployeeMissingCerts
}: ERPCrewSelectorProps) => {
  const { setFieldValue } = useFormikContext<ERPJobFormValues>();
  const {
    loadingJobRoles,
    setEmployeeSearchTerm,
    loadingEmployees,
    buildEmployeeOptionsForRole,
    handleDeleteCrewField,
    handleRoleChange,
    handleEmployeeSelect,
    supportedJobRoles,
    crewMembers
  } = useERPCrewSelector({ crewFields, setCrewFields, startDate, customerId, jobSiteId });

  useEffect(() => {
    if (loadingEmployees) return;
    if (!crewMembers?.length) return;

    let changed = false;
    const next = crewFields.map((crew) => {
      if (crew.employeeId == null || crew.roleId == null) return crew;
      const member = crewMembers.find((e) => e.id === crew.employeeId);
      if (!member) return crew;
      if (crewMemberHasJobRole(member, crew.roleId)) return crew;
      changed = true;
      return { ...crew, employeeId: null, employeeName: '', status: undefined };
    });

    if (changed) setCrewFields(next);
  }, [crewMembers, crewFields, loadingEmployees, setCrewFields]);

  useEffect(() => {
    const employees: GroupEmployee[] = crewFields
      .filter((crew) => crew.roleId != null && crew.employeeId != null)
      .map((crew) => ({
        employeeId: crew.employeeId!,
        employeeName: crew.employeeName || null,
        roleId: crew.roleId!,
        roleName: crew.roleName || `Role ${crew.roleId}`,
        status: crew.status
      }));
    setFieldValue('employees', employees, false);
  }, [crewFields, setFieldValue]);

  const showComplianceWarning =
    isEmployeeCompliant != null &&
    crewFields.some((crew) => {
      if (crew.employeeId == null || crew.roleId == null) return false;
      return !isEmployeeCompliant({ id: crew.employeeId }, crew.roleId);
    });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {showComplianceWarning && <CrewComplianceWarningAlert />}
      {crewFields.map((crew) => (
        <CrewFieldRow
          key={crew.rowId}
          crew={crew}
          supportedJobRoles={supportedJobRoles}
          loadingJobRoles={loadingJobRoles}
          loadingEmployees={loadingEmployees}
          crewMembers={crewMembers}
          buildEmployeeOptionsForRole={buildEmployeeOptionsForRole}
          onRoleChange={handleRoleChange}
          onEmployeeSelect={handleEmployeeSelect}
          onDeleteRow={handleDeleteCrewField}
          setEmployeeSearchTerm={setEmployeeSearchTerm}
          isEmployeeCompliant={isEmployeeCompliant}
          getEmployeeMissingCerts={getEmployeeMissingCerts}
        />
      ))}
    </Box>
  );
};
